import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { surveysApi, SurveyReport } from '@/api/surveys';
import TestPageLayout from '@/components/tests/TestPageLayout';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import useScrollProgress from '@/hooks/use-scroll-progress';
import SurveyResponseViewer from './components/SurveyResponseViewer';
import SurveySectionNav from './components/SurveySectionNav';

export default function SurveyResponseView() {
    const { reportId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const scrollProgress = useScrollProgress();

    // Try to get report from location state first
    const initialReport = location.state?.report as SurveyReport | undefined;

    const { data: reportDetail, isLoading: isLoadingReport } = useQuery({
        queryKey: ['survey-report', reportId],
        queryFn: () => surveysApi.getReport(reportId!),
        enabled: !!reportId,
    });

    const report = reportDetail || initialReport;
    const surveyDefinition = reportDetail?.survey;

    if (isLoadingReport && !reportDetail) return <Loading />;

    if (!report) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
                <div className="text-center">
                    <h1 className="mb-2 text-2xl font-bold text-foreground">
                        Relatório não encontrado
                    </h1>
                    <p className="mb-6 text-muted-foreground">
                        O relatório que você está tentando visualizar não existe.
                    </p>
                    <Button onClick={() => navigate('/surveys')}>Voltar para Formulários</Button>
                </div>
            </div>
        );
    }

    if (!surveyDefinition) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
                <div className="text-center">
                    <h1 className="mb-2 text-2xl font-bold text-foreground">
                        Definição do formulário não encontrada
                    </h1>
                    <p className="mb-6 text-muted-foreground">
                        Não foi possível encontrar a estrutura do formulário &quot;
                        {report.surveyName}&quot;.
                    </p>
                    <Button onClick={() => navigate('/surveys')}>Voltar para Formulários</Button>
                </div>
            </div>
        );
    }

    return (
        <TestPageLayout
            progress={scrollProgress}
            onBack={() => navigate('/surveys')}
            mainClassName="max-w-6xl"
        >
            <div className="flex items-start gap-6">
                <SurveySectionNav sections={surveyDefinition.sections} />
                <div className="flex-1 space-y-6">
                    <div className="border-b pb-4">
                        <h1 className="mb-1 text-2xl font-bold text-foreground">
                            {surveyDefinition.name}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            Paciente:{' '}
                            <span className="font-medium text-foreground">
                                {report.patientName}
                            </span>
                        </p>
                    </div>

                    <Alert variant="info">
                        <strong>Visualização de Respostas:</strong> Abaixo estão as respostas
                        enviadas pelo paciente.
                    </Alert>

                    <SurveyResponseViewer
                        survey={surveyDefinition}
                        answers={report.answers || {}}
                    />
                </div>
            </div>
        </TestPageLayout>
    );
}
